import {
  isPlayCrm,
  PLAY_CRM_NAMES,
  type CrmActionConfig,
  type CrmBaseAssignment,
  type CustomGraph,
  type TriggerConfig,
} from '@/types/custom-playbooks';
import type {
  AttioList,
  AttioObjectType,
  HubSpotProperty,
  MartechList,
} from '@/store/features/crm.feature';
import { advancedOwner, advancedOwnerIssues } from './sync-owner-config.util';
import { isAttioId, isCrmFieldName } from './crm-fields';
import { plural } from '@/lib/formatters';

export const CRM_ACTION_OPTIONS = [
  { value: 'sync', label: 'Sync' },
  { value: 'lookup', label: 'Look up' },
  { value: 'update_record', label: 'Update fields' },
  { value: 'create_task', label: 'Create task' },
  { value: 'create_note', label: 'Log note' },
  { value: 'add_to_campaign', label: 'Add to campaign' },
  { value: 'set_owner', label: 'Set owner' },
  { value: 'add_contact_role', label: 'Add contact role' },
] as const;

/** Not on Attio (attio-build-plan D4, D16): its people and companies have no
 *  owner to set, and its deals have no contact roles. */
const ATTIO_HIDDEN_ACTIONS: readonly string[] = [
  'set_owner',
  'add_contact_role',
];

/** The agent play's action select (C 5.2.7 item 1): "Add to list" on
 *  HubSpot and Attio, no person-only actions on a company record, and on
 *  Attio only the actions it has. */
export function crmActionOptions(
  crmType: CrmActionConfig['crmType'],
  company: boolean
) {
  return CRM_ACTION_OPTIONS.filter(
    (row) =>
      (!company ||
        !['add_to_campaign', 'add_contact_role'].includes(row.value)) &&
      (crmType !== 'attio' || !ATTIO_HIDDEN_ACTIONS.includes(row.value))
  ).map((row) => ({
    value: row.value,
    label:
      row.value === 'add_to_campaign' &&
      (crmType === 'hubspot' || crmType === 'attio')
        ? 'Add to list'
        : row.label,
  }));
}

/** The "On" select's words for each target (C 5.2.7 item 2). */
export const CRM_TARGET_LABELS: Readonly<Record<string, string>> = {
  record: 'This person',
  account: 'Their account',
  deal: 'Their open deal',
};

export function crmWriteControlState(
  config: CrmActionConfig,
  blocked: boolean,
  schemaReady: boolean
) {
  const schemaBlocked = config.action === 'update_record' && !schemaReady;
  return {
    blocked: blocked || (config.crmType !== 'attio' && schemaBlocked),
    fieldsBlocked: blocked || schemaBlocked,
  };
}

export function isCrmWriteAction(action: unknown) {
  return CRM_ACTION_OPTIONS.slice(2).some((option) => option.value === action);
}

/** Rebuild the native closed action config; Sync keys cannot cross actions. */
export function freshCrmAction(
  action: CrmActionConfig['action'],
  crmType: CrmActionConfig['crmType'],
  company = false
): CrmActionConfig {
  const base = { action, crmType };
  if (action === 'sync' || action === 'lookup')
    return {
      ...base,
      objectType: company ? 'Account' : 'Contact',
      mappingMode: 'default',
      mappings: [],
      ...(action === 'sync' ? { assignment: { mode: 'keep' } as const } : {}),
      ...(action === 'lookup'
        ? {
            lookupConfig: {
              crm_type: crmType,
              object_type: company
                ? ('Account' as const)
                : ('Contact' as const),
              identifiers: company
                ? (['crm_id', 'domain'] as const).slice()
                : (['email'] as const).slice(),
              options: {},
            },
          }
        : {}),
    };
  if (action === 'add_to_campaign')
    return {
      ...base,
      target: 'record',
      campaign: { id: '' },
      writeSafety: 'inherit',
    };
  if (action === 'add_contact_role')
    return {
      ...base,
      target: 'deal',
      role: '',
      primary: false,
      writeSafety: 'inherit',
    };
  const target = company ? 'account' : 'record';
  if (action === 'update_record')
    return { ...base, target, fields: [], writeSafety: 'inherit' };
  if (action === 'set_owner')
    return {
      ...base,
      target,
      assignment: { mode: 'keep' },
      writeSafety: 'inherit',
    };
  if (action === 'create_note')
    return {
      ...base,
      target,
      once: company ? 'per_target' : 'per_record',
      note: { title: '', body: '' },
    };
  // Attio tasks have no type or priority, and only a deal has an owner, so
  // a new one goes to nobody until a member is picked.
  if (crmType === 'attio')
    return {
      ...base,
      target,
      once: company ? 'per_target' : 'per_record',
      task: { subject: '', dueInDays: 1 },
      assignment: { mode: 'keep' },
    };
  return {
    ...base,
    target,
    once: company ? 'per_target' : 'per_record',
    task: { subject: '', type: 'call', priority: 'normal', dueInDays: 1 },
    assignment: { mode: 'record_owner', fallback: { mode: 'keep' } },
  };
}

export function crmWriteTargets(
  action: CrmActionConfig['action'],
  company: boolean
) {
  if (action === 'add_to_campaign') return company ? [] : ['record'];
  if (action === 'add_contact_role') return company ? [] : ['deal'];
  return [
    ...(company ? [] : ['record']),
    'account',
    ...(action === 'set_owner' ? [] : ['deal']),
  ];
}

export function crmWriteObject(config: CrmActionConfig, original: string) {
  if (config.target === 'account') return 'Account';
  if (config.target === 'deal')
    return config.crmType === 'salesforce' ? 'Opportunity' : 'Deal';
  return original;
}

const RESERVED = new Set([
  'Id',
  'id',
  'hs_object_id',
  'OwnerId',
  'hubspot_owner_id',
  'AccountId',
  'cleanlist_id',
  'cleanlist_id__c',
  'Cleanlist_List__c',
  'cleanlist_list',
]);

/** Attio attributes a field update never writes: the record's id, and the
 *  deal owner, which is an owner assignment (the API refuses both). */
const ATTIO_UNWRITTEN = new Set(['record_id', 'owner']);

function attioWritableName(name: string) {
  return isCrmFieldName(name, 'attio') && !ATTIO_UNWRITTEN.has(name);
}

export function writableCrmFields(crm: string, properties: HubSpotProperty[]) {
  return properties.filter(
    (field) =>
      /^[A-Za-z][A-Za-z0-9_]{0,199}$/.test(field.name) &&
      !RESERVED.has(field.name) &&
      !field.read_only &&
      !field.hidden &&
      (crm !== 'salesforce' || field.updateable === true) &&
      (crm !== 'attio' || attioWritableName(field.name))
  );
}

export type CrmFieldRow = NonNullable<CrmActionConfig['fields']>[number];

/** An Update fields row that fills its field by round robin, not a value. */
export function isRoundRobinRow(row: CrmFieldRow): boolean {
  return row.assignment != null;
}

/** The writable fields a round robin can fill: those the properties response
 *  says hold a CRM user (`holds_user`: a Salesforce User lookup, a HubSpot
 *  owner property), the rule the run checks. A response that says it of no
 *  field (an older API or cache) can't tell, so it offers every Salesforce
 *  lookup and HubSpot single-select property, and the run checks the field. */
export function roundRobinFields(crm: string, writable: HubSpotProperty[]) {
  if (crm !== 'salesforce' && crm !== 'hubspot') return writable;
  if (writable.some((field) => typeof field.holds_user === 'boolean'))
    return writable.filter((field) => field.holds_user === true);
  const lower = (value?: string) => (value ?? '').toLowerCase();
  return writable.filter((field) =>
    crm === 'salesforce'
      ? lower(field.type) === 'reference'
      : lower(field.type) === 'enumeration' &&
        !['checkbox', 'booleancheckbox'].includes(lower(field.field_type))
  );
}

/** A round robin across one to twenty distinct chosen CRM users, and
 *  nothing else. */
function roundRobinPool(assignment: CrmBaseAssignment | undefined): boolean {
  const ids = assignment?.userIds;
  return (
    assignment?.mode === 'round_robin_selected' &&
    Object.keys(assignment).every(
      (key) => key === 'mode' || key === 'userIds'
    ) &&
    Array.isArray(ids) &&
    ids.length >= 1 &&
    ids.length <= 20 &&
    new Set(ids).size === ids.length &&
    ids.every((id) => typeof id === 'string' && !!id.trim() && id.length <= 160)
  );
}

/** The Attio object a write's fields come from: people, companies or deals.
 *  Null for anything else (Attio has no Lead). */
export function attioWriteObject(objectType: string): AttioObjectType | null {
  if (objectType === 'Contact') return 'people';
  if (objectType === 'Account') return 'companies';
  if (objectType === 'Deal') return 'deals';
  return null;
}

/** Attio lists a person can join: lists whose parent is people. A list of
 *  companies, deals or a custom object would refuse the person. */
export function attioPeopleLists(rows: AttioList[]) {
  return rows.filter((row) => isAttioId(row.id) && row.object === 'Contact');
}

/** Unknown and dynamic lists remain unavailable for the new write picker. */
export function staticContactLists(rows: MartechList[]) {
  return rows.filter(
    (row) =>
      typeof row.id === 'string' &&
      /^[0-9]{1,30}$/.test(row.id) &&
      row.raw?.objectTypeId === '0-1' &&
      (row.static === true || row.raw?.processingType === 'MANUAL')
  );
}

/** What an Attio write can't carry, as the API refuses it (A3.6). */
function attioWriteError(config: CrmActionConfig): string | null {
  if (ATTIO_HIDDEN_ACTIONS.includes(config.action))
    return 'Not available on Attio';
  if (config.action === 'create_task' || config.action === 'create_note') {
    if (config.linkCompanyAndDeal === true)
      return 'Attio deals have no open stage to link to';
    if (
      config.task &&
      (config.task.type !== undefined || config.task.priority !== undefined)
    )
      return 'Attio tasks have no type or priority';
  }
  const owner = config.action === 'create_task' ? config.assignment : undefined;
  if (owner && advancedOwner(owner)) return 'Attio records have no owner';
  if (owner?.mode === 'record_owner' && config.target !== 'deal')
    return 'Deal owner on deal tasks only';
  if (
    config.action === 'update_record' &&
    (config.fields ?? []).some(
      (row) => row.field && !attioWritableName(row.field)
    )
  )
    return 'Pick a writable field';
  if (
    config.action === 'update_record' &&
    (config.fields ?? []).some(isRoundRobinRow)
  )
    return 'Round robin not available on Attio';
  if (config.action === 'add_to_campaign') {
    if (config.campaign?.status !== undefined)
      return 'Attio list adds set no status';
    if (config.campaign?.id && !isAttioId(config.campaign.id))
      return 'Pick an Attio list';
  }
  return null;
}

export function crmWriteConfigError(config: CrmActionConfig): string | null {
  if (!isCrmWriteAction(config.action)) return null;
  if (config.crmType === 'attio') {
    const attio = attioWriteError(config);
    if (attio) return attio;
  }
  const base = ['action', 'crmType', 'target'];
  const allowed = new Set([
    ...base,
    ...(config.action === 'create_task'
      ? ['once', 'task', 'assignment']
      : config.action === 'create_note'
        ? ['once', 'note']
        : ['writeSafety']),
    ...(config.action === 'update_record'
      ? ['fields']
      : config.action === 'set_owner'
        ? ['assignment']
        : config.action === 'add_to_campaign'
          ? ['campaign']
          : config.action === 'add_contact_role'
            ? ['role', 'primary']
            : []),
  ]);
  if (Object.keys(config).some((key) => !allowed.has(key)))
    return 'Unsupported settings';
  if (config.target === 'new_record') return 'Pick a target';
  if (config.action === 'set_owner' && config.target === 'deal')
    return 'This person or their account only';
  if (
    config.action === 'add_to_campaign' &&
    config.target &&
    config.target !== 'record'
  )
    return 'This person only';
  if (
    config.action === 'add_contact_role' &&
    config.target &&
    config.target !== 'deal'
  )
    return 'Their open deal only';
  if (config.action === 'update_record') {
    const fields = config.fields;
    if (!Array.isArray(fields) || fields.length < 1) return 'Pick a field';
    if (fields.length > 20) return 'Up to 20 fields';
    if (fields.some((row) => !row.field)) return 'Pick a field';
    if (
      fields.some(
        (row) =>
          !/^[A-Za-z][A-Za-z0-9_]{0,199}$/.test(row.field) ||
          RESERVED.has(row.field)
      )
    )
      return 'Pick a writable field';
    if (new Set(fields.map((row) => row.field)).size !== fields.length)
      return 'Each field once';
    const pooled = fields.filter(isRoundRobinRow);
    if (pooled.length > 1) return 'One round robin per step';
    if (pooled.some((row) => row.value != null))
      return 'A value or a round robin, not both';
    if (
      fields.some(
        (row) =>
          !isRoundRobinRow(row) &&
          (typeof row.value !== 'string' || row.value.length > 8000)
      )
    )
      return 'Value up to 8,000 characters';
    if (
      pooled.some(
        (row) => (row.writeMode ?? 'fill_if_empty') !== 'fill_if_empty'
      )
    )
      return 'Round robin fills empty fields only';
    if (pooled.some((row) => !roundRobinPool(row.assignment)))
      return 'Pick 1 to 20 users';
  }
  if (config.action === 'set_owner' || config.action === 'create_task') {
    const owner =
      config.assignment ??
      (config.action === 'create_task'
        ? { mode: 'record_owner' as const, fallback: { mode: 'keep' as const } }
        : undefined);
    if (!owner) return 'Pick an owner';
    if (owner.mode === 'record_owner' && config.action !== 'create_task')
      return 'Record owner on tasks only';
    if (advancedOwner(owner) || owner.mode === 'record_owner') {
      const errors = advancedOwnerIssues(owner);
      if (errors.length) return errors[0];
    } else if (owner.mode === 'specific' && owner.userIds?.length !== 1)
      return 'Pick one owner';
    else if (
      owner.mode === 'round_robin_selected' &&
      (!Array.isArray(owner.userIds) ||
        owner.userIds.length < 1 ||
        owner.userIds.length > 20 ||
        new Set(owner.userIds).size !== owner.userIds.length)
    )
      return 'Pick 1 to 20 owners';
  }
  if (config.task?.dueDate !== undefined && !config.task.dueDate)
    return 'Pick a due date field';
  if (
    config.task?.dueInDays !== undefined &&
    (!Number.isInteger(config.task.dueInDays) ||
      config.task.dueInDays < 0 ||
      config.task.dueInDays > 30)
  )
    return 'Due in 0 to 30 days';
  if (config.action === 'create_task') {
    if (!config.task?.subject?.trim()) return 'Add a subject';
    if (config.task.subject.length > 255) return 'Subject up to 255 characters';
    if ((config.task.body?.length ?? 0) > 4000)
      return 'Body up to 4,000 characters';
  }
  if (config.action === 'create_note') {
    if (!config.note?.title?.trim()) return 'Add a title';
    if (config.note.title.length > 120) return 'Title up to 120 characters';
    if (!config.note.body?.trim()) return 'Add a body';
    if (config.note.body.length > 4000) return 'Body up to 4,000 characters';
  }
  if (config.action === 'add_to_campaign' && !config.campaign?.id)
    return config.crmType === 'salesforce' ? 'Pick a campaign' : 'Pick a list';
  if (config.action === 'add_contact_role') {
    if (!config.role?.trim()) return 'Add a role';
    if (config.role.length > 60) return 'Role up to 60 characters';
    if (config.crmType === 'hubspot' && config.primary)
      return 'Primary on Salesforce only';
  }
  return null;
}

/**
 * C 5.2.7 item 9: a write needs the record read in its CRM first, from a CRM
 * trigger, Look up or Sync above it on the same CRM, as the server's
 * `crm_target_unresolved` warning reads it.
 */
export function crmWriteSourceIssue(
  graph: CustomGraph | null | undefined,
  nodeId: string,
  config: CrmActionConfig
): string | null {
  if (!graph || !isCrmWriteAction(config.action)) return null;
  // Every step above this one (graph-ops imports this module, so walk here).
  const above = new Set<string>();
  const stack = [nodeId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const edge of graph.edges)
      if (edge.target === id && !above.has(edge.source)) {
        above.add(edge.source);
        stack.push(edge.source);
      }
  }
  const read = graph.nodes.some((node) => {
    if (!above.has(node.id)) return false;
    if (node.type === 'trigger') {
      const trigger = node.config as TriggerConfig;
      return (
        trigger.source === 'crm' && trigger.crm?.crmType === config.crmType
      );
    }
    if (node.type !== 'crm_action') return false;
    const step = node.config as CrmActionConfig;
    return (
      step.crmType === config.crmType &&
      (step.action === 'lookup' || step.action === 'sync')
    );
  });
  return read ? null : 'Add a Look up or Sync before this step';
}

/** A saved HubSpot destination that is a dynamic list, not a static one. */
export function dynamicListPicked(
  config: CrmActionConfig,
  lists: MartechList[] | undefined
): boolean {
  if (
    config.action !== 'add_to_campaign' ||
    config.crmType !== 'hubspot' ||
    !config.campaign?.id ||
    !lists
  )
    return false;
  const id = config.campaign.id;
  return (
    lists.some((row) => row.id === id) &&
    !staticContactLists(lists).some((row) => row.id === id)
  );
}

/** The CRM's name, or "CRM" for a saved value no CRM has. */
export function crmName(crm: unknown): string {
  return isPlayCrm(crm) ? PLAY_CRM_NAMES[crm] : 'CRM';
}

/** Who a task or owner write goes to, in a few words. Keep says nothing. */
function ownerWords(owner: CrmActionConfig['assignment']): string | null {
  switch (owner?.mode) {
    case 'record_owner':
      return 'record owner';
    case 'account_owner':
      return 'account owner';
    case 'from_field':
      return 'owner from field';
    case 'rules':
      return plural(owner.rules?.length ?? 0, 'rule');
    case 'round_robin_all':
      return 'round-robin';
    case 'round_robin_selected':
      return `round-robin (${plural(owner.userIds?.length ?? 0, 'owner')})`;
    case 'specific':
      return 'fixed owner';
    default:
      return null;
  }
}

function dueWords(task: CrmActionConfig['task']): string | null {
  if (task?.dueDate) return 'due on a date field';
  const days = task?.dueInDays;
  if (typeof days !== 'number' || !Number.isInteger(days)) return null;
  return days === 0 ? 'due today' : `due in ${plural(days, 'day')}`;
}

/** The card summary of a write action (C 5.2.7 item 8): "Create Salesforce
 *  task · record owner · due in 1 day", "Update 2 account fields". Without
 *  `named` the CRM goes unsaid ("Create task"), for words that already lead
 *  with it. */
export function crmWriteSummary(config: CrmActionConfig, named = true): string {
  const crm = named ? ` ${crmName(config.crmType)}` : '';
  const target =
    config.target === 'account'
      ? 'account'
      : config.target === 'deal'
        ? 'deal'
        : null;
  switch (config.action) {
    case 'update_record': {
      const count = config.fields?.length ?? 0;
      return `Update ${count.toLocaleString()} ${target ? `${target} ` : ''}field${count === 1 ? '' : 's'}`;
    }
    case 'set_owner':
      return [
        target ? `Set ${target} owner` : 'Set owner',
        ownerWords(config.assignment),
      ]
        .filter(Boolean)
        .join(' · ');
    case 'create_task':
      return [
        `Create${crm} task`,
        target,
        ownerWords(
          config.assignment ?? {
            mode: 'record_owner',
            fallback: { mode: 'keep' },
          }
        ),
        dueWords(config.task),
      ]
        .filter(Boolean)
        .join(' · ');
    case 'create_note':
      return [`Log${crm} note`, target].filter(Boolean).join(' · ');
    case 'add_to_campaign':
      return config.campaign?.name
        ? `Add to ${config.campaign.name}`
        : config.crmType === 'salesforce'
          ? 'Pick a campaign'
          : 'Pick a list';
    case 'add_contact_role':
      return config.role?.trim()
        ? `Add ${config.role.trim()} role`
        : 'Add contact role';
    default:
      return '';
  }
}
